import {objectFromEntries} from '@benchling/js-utils';
import _, {Dictionary} from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import * as context from 'Context';
import {histogram} from 'metrics/Metrics';
import {ProtocolVersion, Selector} from 'notebook/ELNConstants';
import {
  areEvalSnapshotDepsEqual,
  getNoteTableMap,
  shouldRenderFromEvaluationSnapshot,
} from 'notebook/mediocre/util/TableMapUtil';
import * as StepConstants from 'notebook/protocols/StepConstants';
import {SimpleTableDoc} from 'notebook/simple-tables/useSimpleEvaluation';
import excess from 'notebook/tables/excess';
import {FullTableMap, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import Resolver, {mergeResolutions} from 'notebook/tables/excess/Resolver';
import {runEvalution} from 'notebook/tables/excess/runEval';
import {NoteRuntimeSnapshot, QueryResolution} from 'notebook/tables/TableStoreTransforms';
import {
  getNumberOfTableCellsInTableMap,
  tableCellCountBucket,
} from 'notebook/util/analytics/TableAnalyticsUtils';
import {BioentityNote, EntryNote, Note} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';

import {
  DocumentMapper,
  DocumentMapVisitor,
  DocumentTraverser,
  DocumentTraverseVisitor,
} from './DocumentTraversal';

// TODO(BNCH-13228): Clean up interface with plugins and surface standalone tables
function getStepTableMap(protocolVersion: ProtocolVersion): FullTableMap {
  const tableMap: FullTableMap = {};
  for (const step of protocolVersion.steps) {
    if (step.level === StepConstants.StepLevel.TABLE && step.table) {
      tableMap[step.table.name] = step.table;
    }
  }
  return tableMap;
}

/**
 * Description of an evaluation for the document with the given cid.
 */
export interface TableEvaluation {
  cid: string;
  evaluationSnapshot: OutputTableMap;
  syncResolutions: Array<QueryResolution>;
  asyncResolutions: Array<Promise<QueryResolution>>;
}

/**
 * `Resolver.resolveFromResolutionQuery` is very tricky to stub.
 *
 * It's an instance method, so to stub it in each instance, we need to stub `Resolver.prototype`.
 * But `resolveFromResolutionQuery` doesn't actually exist on the prototype, because it's declared
 * as an arrow function!
 *
 * Instead, we make `DocumentEvaluationUpdater` (DEU) construct `Resolver` in a roundabout way,
 * using a factory method. We can then stub that factory method to set up a stub that we can use.
 */
export const stubbableFunctions = {
  makeResolver: (...args: ConstructorParameters<typeof Resolver>): Resolver => new Resolver(...args),
};

/**
 * Visitor of hierarchical documents that produces a list of the evaluated tables.
 */
export class EvaluationVisitor implements DocumentTraverseVisitor {
  tableEvals: Array<TableEvaluation>;
  fileId: string;

  constructor(fileId: string) {
    this.fileId = fileId;
    this.tableEvals = [];
  }

  runEval(
    itemCid: string,
    fullTableMap: FullTableMap,
    selectors: Array<Selector>,
    noteRuntimeSnapshot: NoteRuntimeSnapshot
  ): void {
    const tableNameToProvenanceUuid = objectFromEntries(
      Object.entries(fullTableMap).map(([tableName, table]) => [tableName, table.provenanceUuid ?? null])
    );
    this.tableEvals.push(
      runEvalution({
        itemCid,
        fileId: this.fileId,
        fullTableMap,
        selectors,
        noteRuntimeSnapshot,
        tableNameToProvenanceUuid,
      })
    );
  }

  visitNote(item: Note, oldItem: Note | null): void {
    const startTime = Date.now();
    const noteTableMap = getNoteTableMap(this.fileId, item);

    if (
      !context.get('forceExcessRevaluate', false) &&
      shouldRenderFromEvaluationSnapshot(this.fileId, oldItem, item, noteTableMap)
    ) {
      return;
    }

    this.runEval(item.cid, noteTableMap, item.selectors, item.noteRuntimeSnapshot as NoteRuntimeSnapshot);
    this.recordEvalAnalytics(startTime, this.fileId, getNumberOfTableCellsInTableMap(noteTableMap));
  }

  private recordEvalAnalytics(startTime: number, fileId: string, cellCount: number): void {
    if (APIUtil.isEntry(fileId)) {
      histogram('deu_run_eval_entry', Date.now() - startTime, {
        cell_count_bucket: tableCellCountBucket(cellCount),
      });
    }
  }

  visitProtocolVersion(item: ProtocolVersion, oldItem: ProtocolVersion | null): void {
    if (areEvalSnapshotDepsEqual(this.fileId, oldItem, item)) {
      return;
    }
    this.runEval(
      item.cid,
      getStepTableMap(item),
      item.selectors,
      item.noteRuntimeSnapshot as NoteRuntimeSnapshot
    );
  }

  visitSimpleTableDoc(item: SimpleTableDoc, oldItem: SimpleTableDoc | null): void {
    if (areEvalSnapshotDepsEqual(this.fileId, oldItem, item)) {
      return;
    }
    this.runEval(item.cid, item.fullTableMap, item.selectors, item.noteRuntimeSnapshot);
  }
}

/**
 * Describes an update to the document with the given cid.
 */
export interface EvalMergeTuple {
  cid: string;
  mergeUpdateInto: (noteRuntimeSnapshot: NoteRuntimeSnapshot) => NoteRuntimeSnapshot;
}

/**
 * Visitor of a hierarchical document that merges in noteRuntimeSnapshot into the appropriate base document.
 */
export class EvalMergeVisitor implements DocumentMapVisitor {
  evalMergeTuples: Dictionary<EvalMergeTuple>;

  constructor(evalMergeTuples: Array<EvalMergeTuple>) {
    this.evalMergeTuples = _.indexBy(evalMergeTuples, 'cid');
  }

  visitItem<T extends {noteRuntimeSnapshot: NoteRuntimeSnapshot; cid: string}>(item: T): T {
    if (!this.evalMergeTuples[item.cid]) {
      return item;
    }
    const {mergeUpdateInto} = this.evalMergeTuples[item.cid];
    return {
      ...item,
      noteRuntimeSnapshot: mergeUpdateInto(item.noteRuntimeSnapshot),
    };
  }

  visitNote<T extends BioentityNote | EntryNote>(item: T): T {
    return this.visitItem(item);
  }

  visitProtocolVersion(item: ProtocolVersion): ProtocolVersion {
    return this.visitItem(item);
  }

  visitSimpleTableDoc(item: SimpleTableDoc): SimpleTableDoc {
    return this.visitItem(item);
  }
}

/**
 * This class coordinates re-evaluation and persistence of all noteRuntimeSnapshots contained within a
 * "top-level document" (TRoot, e.g WorkflowEntryTemplate, Entry, Protocol) which wraps a known
 * "hierarchical document" (TDoc, e.g. TemplateVersion, EntryVersion, ProtocolVersion).
 *
 * A subclass must be implemented for each "top-level document" type.
 */
export default abstract class DocumentEvaluationUpdater<TRoot, TDoc> {
  private readonly hasComponentEvaluationFallback: boolean;

  abstract traverse(evalTraverser: DocumentTraverser, newDoc: TDoc, oldDoc: TDoc | null): void;
  abstract mapDoc(mapper: DocumentMapper, doc: TDoc): TDoc;
  abstract getDoc(root: TRoot): TDoc;
  abstract updatedRoot(root: TRoot, doc: TDoc): TRoot;

  /**
   * @param options.hasComponentEvaluationFallback - Indicates if this class should produce
   * evaluation in the case ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL is disabled. If true, indicates
   * the component that has a fallback, so the updater will no-op. If false, the updates will
   * always produce evaluation. This is true by default, as that is more common in our codebase.
   */
  constructor(
    private readonly fileId: string,
    private readonly getRoot: () => TRoot | null,
    private readonly onAsyncUpdateComplete: (newDoc: TDoc) => void,
    {hasComponentEvaluationFallback = true}: {hasComponentEvaluationFallback?: boolean} = {}
  ) {
    this.hasComponentEvaluationFallback = hasComponentEvaluationFallback;
  }

  _getSyncUpdates(tableEvals: Array<TableEvaluation>): Array<EvalMergeTuple> {
    return tableEvals.map((syncEval) => ({
      cid: syncEval.cid,
      mergeUpdateInto(noteRuntimeSnapshot: NoteRuntimeSnapshot): NoteRuntimeSnapshot {
        return {
          ...noteRuntimeSnapshot,
          evaluationSnapshot: syncEval.evaluationSnapshot,
          excessVersion: excess.EXCESS_VERSION,
          queryResolutions: syncEval.syncResolutions,
        };
      },
    }));
  }

  async _waitForResolutionUpdates(syncEval: TableEvaluation): Promise<EvalMergeTuple | null> {
    const newQueryResolutions = await Promise.all(syncEval.asyncResolutions);
    if (newQueryResolutions.length === 0) {
      return null;
    }
    return {
      cid: syncEval.cid,
      mergeUpdateInto(noteRuntimeSnapshot: NoteRuntimeSnapshot): NoteRuntimeSnapshot {
        return {
          ...noteRuntimeSnapshot,
          queryResolutions: mergeResolutions(noteRuntimeSnapshot.queryResolutions, newQueryResolutions),
        };
      },
    };
  }

  async _runAsyncUpdates(tableEvals: Array<TableEvaluation>): Promise<void> {
    const tupleOrNulls = await Promise.all(
      tableEvals.map((syncEval) => this._waitForResolutionUpdates(syncEval))
    );
    const resMergeTuples = tupleOrNulls.filter((tupleOrNull) => tupleOrNull != null);
    if (!resMergeTuples.length) {
      return;
    }
    const root = this.getRoot();
    if (!root) {
      notifications.error('Cannot resolve loading cells when entry is not editable.');
      return;
    }

    // TODO(BNCH-14583): In the bootstrap case, currently all subclasses of DocumentEvaluationUpdater will
    // specify {bumpVersion: false} when performing async resolutions, which basically will silently update
    // the entry without persisting it. This is bad. We should consider either:
    //   a. blocking edits until bootstrapping finishes (arguably good UX but more complex implementation)
    //   b. cancel subsequent bootstrap updates if there has been an edit (bad UX, easy implementation)
    //   c. bump the version / attribute the resolution to the user editing the entry
    // See EntryStore-test.ts: 'handle async bootstrap (withInterimUpdate=true)
    const liveDoc = this.getDoc(root);
    const resMapper = new DocumentMapper(new EvalMergeVisitor(resMergeTuples));
    const liveDocWithEval = this.mapDoc(resMapper, liveDoc);
    if (window.debug) {
      // eslint-disable-next-line no-console
      console.info(`[EXCESS] [${this.fileId}] Persisting new async resolution...`);
    }
    this.onAsyncUpdateComplete(liveDocWithEval);
  }

  _runOnDoc(newDoc: TDoc, oldDoc: TDoc | null): TDoc {
    // From the source, re-evaluate noteRuntimeSnapshot.evaluationTables. Surfacing oldDoc allows us to
    // skip evaluation when it hasn't changed.
    const visitor = new EvaluationVisitor(this.fileId);
    const evalTraverser = new DocumentTraverser(visitor);
    this.traverse(evalTraverser, newDoc, oldDoc);
    const evalMergeTuples = this._getSyncUpdates(visitor.tableEvals);

    // Kick off any async re-evals.
    this._runAsyncUpdates(visitor.tableEvals);

    // Return the entry with synchronous edits applied
    const mapper = new DocumentMapper(new EvalMergeVisitor(evalMergeTuples));
    const newDocVersion = this.mapDoc(mapper, newDoc);

    return newDocVersion;
  }
  run(root: TRoot, oldRoot: TRoot | null): TRoot {
    // TODO: When ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL is fully rolled out, there should no longer be
    // component evaluation, so we should be able to remove hasComponentEvaluationFallback as well.
    if (!BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL && this.hasComponentEvaluationFallback) {
      return root;
    }

    const doc = this.getDoc(root);
    const oldDoc = oldRoot ? this.getDoc(oldRoot) : null;
    const newDoc = this._runOnDoc(doc, oldDoc);
    return this.updatedRoot(root, newDoc);
  }
}
